import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { ErrorAlert, SuccessAlert } from "@shared/ui/alerts";
import { InputController } from "@shared/ui/form";
import InputDatePickerController from "@shared/ui/form/component/InputDatePickerController";
import { userContainPermission } from "@shared/utils/permission";
import { usePermission } from "@shared/hooks/usePermission";
import { useCreateTimeEntry } from "../hooks/useCreateTimeEntry";
import { useActivities } from "../hooks/useActivities";
import { useAssignedProjects } from "../hooks/useAssignedProjects";
import { useProjectIssues } from "../hooks/useProjectIssues";
import { useStoredProjectAssignments } from "../hooks/useStoredProjectAssignments";
import { buildChargedHourSchema } from "../schema/chargedHourSchema";
import { buildChargedHourEntry } from "../lib/timeEntryMappers";
import { isZeroHourActivity } from "../lib/hourRules";
import { hasNoProjectOptions } from "../lib/projectAssignments";
import { SelectProjectRegisterHour } from "./SelectProjectRegisterHour";
import { EntitySelect } from "./EntitySelect";
import { UnmatchedAssignmentsAlert } from "./UnmatchedAssignmentsAlert";

import type { ChargedHourValues } from "../schema/chargedHourSchema";
import type { CreateTimeEntryPayload } from "../api/timeEntryApi";

import type { TFunction } from "i18next";

export interface ChargedHourDialogProps {
  handleClose: () => void;
  open: boolean;
  notLoading: () => void;
  loading: () => void;
  reloadInitialMissingHour: () => void;
  t: TFunction;
}

export const ChargedHourDialog = ({
  handleClose,
  open,
  notLoading,
  loading,
  reloadInitialMissingHour,
  t,
}: ChargedHourDialogProps) => {
  const [hoursFieldVisible, setHoursFieldVisible] = useState(true);

  const { permissionList } = usePermission("Perfil de Imputacion");
  const existPermission = (permission: string) => userContainPermission(permission, permissionList);
  const hasAssignedProjectPermission =
    existPermission("Estructura") || existPermission("Estructura Operativa");

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ChargedHourValues>({
    resolver: yupResolver(
      buildChargedHourSchema({
        isFestive: () => !hoursFieldVisible,
        hasAssignedProjectPermission,
        t,
      })
    ) as never,
  });

  const projectId = watch("projectId");
  const activityId = watch("activityId");

  const { data: activityList } = useActivities(open);
  const { data: assignedProjectList } = useAssignedProjects(open);
  const { data: projectIssues } = useProjectIssues(projectId);
  const issueId = projectIssues?.[0]?.id;

  const {
    all: listProject,
    staffing,
    projects,
    unmatched,
    supportReference,
  } = useStoredProjectAssignments(open);

  // Festivo / licencia sin goce ocultan el campo de horas y fuerzan 0.
  // `activityId` es `string` una vez casteado por yup al enviar, pero el valor crudo que
  // devuelve `watch()` acá es el id numérico del <MenuItem> (ver useProjectIssues.ts para
  // el mismo caso). `Number(...)` documenta ese valor real sin cambiar el resultado.
  useEffect(() => {
    if (Number(activityId) > 0 && activityList) {
      const activity = activityList.find((record) => record.id === Number(activityId));
      if (isZeroHourActivity(activity)) {
        setValue("hours", 0);
        setHoursFieldVisible(false);
      } else {
        setHoursFieldVisible(true);
      }
    }
  }, [activityId, activityList, setValue]);

  const onClose = () => {
    handleClose();
    reset({
      projectId: "",
      activityId: "",
      assignedProjectId: "",
      spentOn: new Date(),
      hours: 0,
      comment: "",
    });
  };

  const createTimeEntry = useCreateTimeEntry();

  const onRegister = (values: ChargedHourValues) => {
    loading();
    // `issueId` puede ser undefined si el proyecto no tiene issues (comportamiento
    // preexistente sin guard, ver #2572); se documenta con `!` en vez de forzar un
    // guard nuevo que cambiaría el comportamiento.
    const entry = buildChargedHourEntry(values, { issueId: issueId!, hoursVisible: hoursFieldVisible });
    onClose();

    // `buildChargedHourEntry` tipa su retorno con los campos de `Partial<ChargedHourValues>`
    // (opcionales) aunque en runtime `values` siempre viene completo desde el form
    // validado por yup; se documenta el cast en vez de aflojar el tipo del builder.
    createTimeEntry.mutate(entry as CreateTimeEntryPayload, {
      onSuccess: () => {
        notLoading();
        SuccessAlert(t("loadHours"), t("doneSuccessfully", { a: "a" })).then(() =>
          reloadInitialMissingHour()
        );
      },
      onError: (error) => {
        notLoading();
        ErrorAlert(t("errorTitle"), error.response?.data?.message ?? t("internalError")).then();
      },
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onRegister)}>
        <DialogTitle>{t("loadHours")}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid size={12}>
              <UnmatchedAssignmentsAlert unmatched={unmatched} supportReference={supportReference} t={t} />
              <FormControl fullWidth sx={{ mt: 1 }}>
                <SelectProjectRegisterHour
                  control={control}
                  defaultValue=""
                  label={t("project")}
                  name="projectId"
                  listValue={listProject}
                  listStaffing={staffing}
                  listProjects={projects}
                  disabled={hasNoProjectOptions(staffing, projects)}
                />
                {errors.projectId && (
                  <span style={{ fontSize: 12, color: "red" }}>{errors.projectId.message}</span>
                )}
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <EntitySelect
                  control={control}
                  defaultValue=""
                  label={t("activities")}
                  name="activityId"
                  listValue={activityList}
                />
                {errors.activityId && (
                  <span style={{ fontSize: 12, color: "red" }}>{errors.activityId.message}</span>
                )}
              </FormControl>
            </Grid>
            {hasAssignedProjectPermission && (
              <Grid size={12}>
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <EntitySelect
                    control={control}
                    defaultValue=""
                    label={t("assignedProject")}
                    name="assignedProjectId"
                    listValue={assignedProjectList}
                  />
                  {errors.assignedProjectId && (
                    <span style={{ fontSize: 12, color: "red" }}>
                      {errors.assignedProjectId.message}
                    </span>
                  )}
                </FormControl>
              </Grid>
            )}
            <Grid size={12}>
              <FormControl fullWidth>
                <InputDatePickerController
                  control={control}
                  defaultValue={new Date()}
                  openTo="month"
                  name="spentOn"
                  label={t("date")}
                  views={["year", "month", "day"]}
                />
                {errors.spentOn && (
                  <span style={{ fontSize: 12, color: "red" }}>{errors.spentOn.message}</span>
                )}
              </FormControl>
            </Grid>
            {hoursFieldVisible && (
              <Grid id="hours-field" size={12}>
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputController
                    control={control}
                    defaultValue={0}
                    label={t("hours")}
                    name="hours"
                  />
                  {errors.hours && (
                    <span style={{ fontSize: 12, color: "red" }}>{errors.hours.message}</span>
                  )}
                </FormControl>
              </Grid>
            )}
            <Grid size={12}>
              <FormControl fullWidth>
                <InputController
                  name="comment"
                  control={control}
                  type="text"
                  defaultValue=""
                  label={t("description")}
                />
                {errors.comment && (
                  <span style={{ fontSize: 12, color: "red" }}>{errors.comment.message}</span>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("cancel")}</Button>
          <Button type="submit">{t("load")}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
